import * as React from 'react'
import * as ReactDOM from 'react-dom'

const isServer = typeof document === 'undefined'
const supportsDSD = !isServer && 'shadowRootMode' in HTMLTemplateElement.prototype

/**
 * We can construct a shadow DOM in two ways:

 * - Imperatively with javascript: the content will not be rendered immediately.
 * - Declaratively with html: the content is rendered when the page first loads.
 *
 * Ideally we want to use the declarative approach in order to:
 *
 * - avoid an initial white screen while client-side javascript is constructing the shadow DOM.
 * - have the page fully rendered on the server.
 *
 * However, there are shortcomings with this in next.js/react related to hydration.
 * See: https://github.com/vercel/next.js/discussions/51717 and https://fullystacked.net/declarative-shadow-dom/.
 * For this reason, we need a more robust solution that can gracefully transition between the imperative and declarative approaches depending if we're on the client or server. The code presented here was based on https://github.com/mayank99/declarative-shadow-dom/.
 */
export default function ShadowRoot({
  children,
  mode = 'open',
}: {
  children: React.ReactNode
  mode?: ShadowRootMode
}) {
  const isClient = useIsClient()

  if (isClient) {
    return <ClientShadowRoot mode={mode}>{children}</ClientShadowRoot>
  }

  // By the time React hydrates, the browser has already removed the template element
  if (supportsDSD) return null

  // for SSR, use DSD template
  return <template {...{ shadowrootmode: mode }}>{children}</template>
}

function ClientShadowRoot({
  children,
  mode = 'open',
}: {
  children: React.ReactNode
  mode?: ShadowRootMode
}) {
  const templateRef = React.useRef<HTMLTemplateElement>(null)
  const [shadowRoot, setShadowRoot] = React.useState<ShadowRoot | null>(null)

  React.useLayoutEffect(() => {
    const parent = templateRef.current?.parentElement
    if (!parent) return

    if (parent.shadowRoot) {
      parent.shadowRoot.replaceChildren()
    }

    queueMicrotask(() => {
      ReactDOM.flushSync(() => {
        setShadowRoot(parent.shadowRoot ?? parent.attachShadow({ mode }))
      })
    })

    return () => setShadowRoot(null)
  }, [mode])

  return shadowRoot ? ReactDOM.createPortal(children, shadowRoot) : <template ref={templateRef} />
}

const noopSubscribe = () => () => {}

function useIsClient() {
  return React.useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false
  )
}
